<template>
  <div class="notification-center">
    <div class="dropdown">
      <button
        class="notification-bell"
        type="button"
        id="notificationDropdown"
        data-bs-toggle="dropdown"
        aria-expanded="false"
        @click="refreshNotifications"
      >
        <i class="bi bi-bell"></i>
        <span v-if="hasUnread" class="notification-badge">{{ unreadCount }}</span>
      </button>

      <div class="dropdown-menu dropdown-menu-end notification-dropdown" aria-labelledby="notificationDropdown">
        <div class="notification-header">
          <h6 class="mb-0">Notifications</h6>
          <div class="notification-actions">
            <button
              v-if="hasUnread"
              @click="markAllAsRead"
              class="btn-action"
              title="Tout marquer comme lu"
            >
              <i class="bi bi-check-all"></i>
            </button>
            <button
              v-if="notifications.length > 0"
              @click="clearAll"
              class="btn-action"
              title="Tout effacer"
            >
              <i class="bi bi-trash"></i>
            </button>
          </div>
        </div>

        <div class="notification-list">
          <div
            v-if="loading"
            class="notification-empty"
          >
            <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
            <p>Chargement...</p>
          </div>
          <div
            v-else-if="notifications.length === 0"
            class="notification-empty"
          >
            <i class="bi bi-bell-slash"></i>
            <p>Aucune notification</p>
          </div>

          <div
            v-for="notification in displayedNotifications"
            :key="notification.notificationId || notification.id"
            :class="['notification-item', { 'unread': !notification.read }]"
            @click="handleNotificationClick(notification)"
          >
            <div class="notification-item-icon" :class="`icon-${notification.type}`">
              <i :class="getIcon(notification.type)"></i>
            </div>
            <div class="notification-item-content">
              <div v-if="notification.title" class="notification-item-title">
                {{ notification.title }}
              </div>
              <div class="notification-item-message">
                {{ notification.message }}
              </div>
              <div class="notification-item-time">
                {{ formatTime(notification.timestamp || notification.created_at) }}
              </div>
            </div>
            <button
              @click.stop="removeNotification(notification.notificationId || notification.id)"
              class="notification-item-remove"
            >
              <i class="bi bi-x"></i>
            </button>
          </div>
        </div>

        <div class="notification-footer">
          <router-link to="/notifications" class="view-all-link">
            <i class="bi bi-arrow-right-circle me-1"></i>
            Voir toutes les notifications
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted } from 'vue'
import { useNotifications } from '../composables/useNotifications'

export default {
  name: 'NotificationCenter',
  setup() {
    const {
      notifications,
      unreadCount,
      hasUnread,
      loading,
      initNotifications,
      fetchNotifications,
      markAsRead,
      markAllAsRead,
      removeNotification,
      clearAllNotifications
    } = useNotifications()

    // Initialize on mount
    onMounted(() => {
      initNotifications()
    })

    const refreshNotifications = () => {
      fetchNotifications()
    }

    // Show only first 5 notifications in dropdown
    const displayedNotifications = computed(() => {
      return notifications.value.slice(0, 5)
    })

    const getIcon = (type) => {
      const icons = {
        success: 'bi bi-check-circle-fill',
        error: 'bi bi-exclamation-circle-fill',
        warning: 'bi bi-exclamation-triangle-fill',
        info: 'bi bi-info-circle-fill'
      }
      return icons[type] || icons.info
    }

    const formatTime = (timestamp) => {
      if (!timestamp) return ''
      const now = new Date()
      const time = new Date(timestamp)
      const diff = Math.floor((now - time) / 1000) // seconds

      if (diff < 60) return 'À l\'instant'
      if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`
      if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)} h`
      if (diff < 604800) return `Il y a ${Math.floor(diff / 86400)} j`
      
      return time.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short'
      })
    }

    const handleNotificationClick = (notification) => {
      if (!notification.read) {
        markAsRead(notification.notificationId || notification.id)
      }
    }

    const clearAll = () => {
      if (confirm('Voulez-vous vraiment effacer toutes les notifications ?')) {
        clearAllNotifications()
      }
    }

    return {
      notifications,
      displayedNotifications,
      unreadCount,
      hasUnread,
      loading,
      refreshNotifications,
      markAllAsRead,
      removeNotification,
      clearAll,
      getIcon,
      formatTime,
      handleNotificationClick
    }
  }
}
</script>


<style scoped>
.notification-center {
  position: relative;
}

.notification-bell {
  position: relative;
  background: none;
  border: none;
  font-size: 20px;
  color: var(--text-muted);
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-bell:hover {
  background-color: rgba(79, 70, 229, 0.1);
  color: var(--primary-color);
}

.notification-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  font-size: 10px;
  font-weight: 600;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  box-shadow: 0 2px 4px rgba(239, 68, 68, 0.3);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.notification-dropdown {
  position: absolute !important;
  top: 100%;
  right: 0;
  width: 380px;
  max-height: 500px;
  padding: 0;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
  margin-top: 8px;
  z-index: 1060;
}

.notification-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid var(--border-color);
  background-color: var(--bg-card);
  border-radius: 12px 12px 0 0;
}

.notification-header h6 {
  font-family: 'Outfit', sans-serif;
  font-weight: 600;
  color: var(--text-main);
  margin: 0;
}

.notification-actions {
  display: flex;
  gap: 8px;
}

.btn-action {
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
  font-size: 16px;
}

.btn-action:hover {
  background-color: rgba(79, 70, 229, 0.1);
  color: var(--primary-color);
}

.notification-list {
  max-height: 400px;
  overflow-y: auto;
  background-color: var(--bg-card);
  border-radius: 0 0 12px 12px;
}

.notification-list::-webkit-scrollbar {
  width: 6px;
}

.notification-list::-webkit-scrollbar-track {
  background: transparent;
}

.notification-list::-webkit-scrollbar-thumb {
  background: var(--border-color);
  border-radius: 3px;
}

.notification-list::-webkit-scrollbar-thumb:hover {
  background: var(--text-muted);
}

.notification-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  color: var(--text-muted);
}

.notification-empty i {
  font-size: 48px;
  margin-bottom: 12px;
  opacity: 0.5;
}

.notification-empty p {
  margin: 0;
  font-size: 14px;
}

.notification-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-color);
  cursor: pointer;
  transition: background-color 0.2s ease;
  position: relative;
}

.notification-item:last-child {
  border-bottom: none;
}

.notification-item:hover {
  background-color: rgba(79, 70, 229, 0.05);
}

.notification-item.unread {
  background-color: rgba(79, 70, 229, 0.03);
}

.notification-item.unread::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: var(--primary-color);
}

.notification-item-icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.notification-item-icon.icon-success {
  background-color: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.notification-item-icon.icon-error {
  background-color: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.notification-item-icon.icon-warning {
  background-color: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

.notification-item-icon.icon-info {
  background-color: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.notification-item-content {
  flex: 1;
  min-width: 0;
}

.notification-item-title {
  font-weight: 600;
  font-size: 13px;
  color: var(--text-main);
  margin-bottom: 2px;
}

.notification-item-message {
  font-size: 13px;
  color: var(--text-muted);
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.notification-item-time {
  font-size: 11px;
  color: var(--text-muted);
  margin-top: 4px;
  opacity: 0.7;
}

.notification-item-remove {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  opacity: 0;
}

.notification-item:hover .notification-item-remove {
  opacity: 1;
}

.notification-item-remove:hover {
  background-color: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

/* Responsive */
@media (max-width: 768px) {
  .notification-dropdown {
    position: absolute !important;
    right: -20px !important; /* Slight offset to account for navbar padding */
    left: auto !important;
    width: calc(100vw - 40px);
    max-width: 350px;
    margin-top: 1rem !important;
    transform: none !important;
  }
}

/* Footer */
.notification-footer {
  padding: 12px 16px;
  border-top: 1px solid var(--border-color);
  background-color: var(--bg-card);
  border-radius: 0 0 12px 12px;
}

.view-all-link {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
}

.view-all-link:hover {
  color: var(--primary-color-dark, #4338ca);
}
</style>
